import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Search, MoreVertical, Pencil, Trash2, Film, Loader2, Upload, PlayCircle, HardDrive, X, Folder, FolderPlus, FolderInput, FileText, FileSpreadsheet, Download, Image as ImageIcon, FileArchive } from "lucide-react";
import { Paginacao, usePaginas } from "@/components/Paginacao";
import { toast } from "sonner";
import { RefreshButton } from "@/components/RefreshButton";
import { VideoUpload } from "@/components/VideoUpload";
import { fmtDuration } from "@/lib/videoDuration";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
export const fmtSize = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${Math.max(1, Math.round((b || 0) / 1024 ** 2))} MB`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");
const isImg = (v) => v.kind === "image";
const isDoc = (v) => v.kind === "doc" || v.kind === "image";   // tudo que não é vídeo
const DocIcon = ({ v, className }) => (["xlsx", "xls", "csv"].includes(v.ext) ? <FileSpreadsheet className={`${className} text-emerald-600`} /> : <FileText className={`${className} ${v.ext === "pdf" ? "text-red-500" : "text-sky-600"}`} />);
const fileUrl = (v) => `${(api.defaults.baseURL || "/api").replace(/\/$/, "")}/admin/videos/${v.id}/download`;
const openDoc = (v) => window.open(fileUrl(v), "_blank");

/** Cursos → Biblioteca: vídeos enviados para a plataforma (ver, renomear, excluir, enviar). */
export default function VideoLibrary() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todos");
  const [uploading, setUploading] = useState(false);
  const [watch, setWatch] = useState(null);   // { video, url }
  const [rename, setRename] = useState(null); // { id, name }
  const [busy, setBusy] = useState(false);
  const [folder, setFolder] = useState("todas");     // todas | sem | id da pasta
  const [tipo, setTipo] = useState("todos");         // todos | video | doc
  const [folderDlg, setFolderDlg] = useState(null);  // { id?, name }
  const [move, setMove] = useState(null);            // { v, folder_id }
  const [foto, setFoto] = useState(null);            // imagem aberta na tela

  const load = useCallback(() => api.get("/admin/videos").then((r) => setData(r.data)).catch(() => setData({ videos: [], total_size: 0 })), []);
  useEffect(() => { load(); }, [load]);

  const inUse = (v) => v.lessons.some((l) => l.uses_it);
  const folders = data?.folders || [];
  const inFolder = (v) => folder === "todas" || (folder === "sem" ? !v.folder_id || !folders.some((f) => f.id === v.folder_id) : v.folder_id === folder);
  const shown = useMemo(() => (data?.videos || []).filter((v) => inFolder(v) &&
    (tipo === "todos" || (tipo === "img" ? isImg(v) : tipo === "doc" ? isDoc(v) && !isImg(v) : !isDoc(v))) &&
    (filter === "todos" || isDoc(v) || (filter === "uso" ? inUse(v) : !inUse(v))) &&
    (!q || v.name.toLowerCase().includes(q.toLowerCase()) || v.lessons.some((l) => `${l.lesson_title} ${l.course_title}`.toLowerCase().includes(q.toLowerCase())))),
  [data, q, filter, folder, tipo]); // eslint-disable-line react-hooks/exhaustive-deps
  const pag = usePaginas(shown, 30);
  const { setPagina } = pag;
  useEffect(() => { setPagina(1); }, [q, filter, folder, tipo, setPagina]);
  const unused = (data?.videos || []).filter((v) => !isDoc(v) && !inUse(v));
  const pasta = folders.find((f) => f.id === folder);
  const contar = (fid) => (data?.videos || []).filter((v) => (fid === "sem" ? !v.folder_id || !folders.some((f) => f.id === v.folder_id) : v.folder_id === fid)).length;
  const salvarPasta = async () => {
    if (!folderDlg.name.trim()) return toast.error("Informe o nome da pasta.");
    setBusy(true);
    try {
      if (folderDlg.id) { await api.patch(`/admin/library/folders/${folderDlg.id}`, { name: folderDlg.name }); toast.success("Pasta renomeada."); }
      else { const r = await api.post("/admin/library/folders", { name: folderDlg.name }); setFolder(r.data.id); toast.success("Pasta criada."); }
      setFolderDlg(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const excluirPasta = async (f) => {
    if (!(await confirmar(`Excluir a pasta "${f.name}"? Os arquivos dela NÃO são apagados: ficam em "Sem pasta".`))) return;
    try { await api.delete(`/admin/library/folders/${f.id}`); toast.success("Pasta excluída."); setFolder("todas"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const baixarZip = async () => {
    const itens = (data?.videos || []).filter(inFolder);
    const nVid = itens.filter((v) => !isDoc(v)).length;
    const nArq = itens.length - nVid;
    let comVideos = 0;
    if (nVid) {
      if (!nArq) { if (!(await confirmar(`Esta pasta só tem vídeos (${nVid}). Baixar todos no .zip? Pode ficar grande e demorar.`))) return; comVideos = 1; }
      else comVideos = (await confirmar(`Incluir também os ${nVid} vídeo(s) no .zip?\n\nOK = com vídeos (fica grande)\nCancelar = só imagens e arquivos`)) ? 1 : 0;
    }
    toast.info("Preparando o .zip… o download começa em instantes.");
    window.open(`${(api.defaults.baseURL || "/api").replace(/\/$/, "")}/admin/library/zip?folder=${encodeURIComponent(folder)}&videos=${comVideos}`, "_blank");
  };
  const mover = async () => {
    try { await api.put(`/admin/videos/${move.v.id}/folder`, { folder_id: move.folder_id }); toast.success("Arquivo movido."); setMove(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const play = async (v) => {
    setWatch({ video: v, url: "" });
    try { const r = await api.get(`/admin/videos/${v.id}/preview`); setWatch({ video: v, url: r.data.url }); }
    catch (e) { toast.error(errMsg(e)); setWatch(null); }
  };
  const saveName = async () => {
    if (!rename.name.trim()) return toast.error("Informe o nome do vídeo.");
    setBusy(true);
    try { await api.patch(`/admin/videos/${rename.id}`, { name: rename.name }); toast.success("Nome atualizado."); setRename(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (v) => {
    const used = v.lessons.filter((l) => l.uses_it);
    const msg = isDoc(v) ? `Excluir o arquivo "${v.name}" (${fmtSize(v.size)})? Ele é apagado de vez do servidor.` : used.length
      ? `O vídeo "${v.name}" está em ${used.length} aula(s):\n\n${used.map((l) => `• ${l.lesson_title} (${l.course_title})`).join("\n")}\n\n` +
        "Se excluir, essas aulas ficam SEM vídeo até você enviar outro. O arquivo é apagado de vez. Excluir mesmo assim?"
      : `Excluir o vídeo "${v.name}" (${fmtSize(v.size)})? O arquivo é apagado de vez do servidor.`;
    if (!(await confirmar(msg))) return;
    try {
      const r = await api.delete(`/admin/videos/${v.id}`, { params: { force: true } });
      toast.success(isDoc(v) ? "Arquivo excluído." : r.data.lessons_cleared ? `Vídeo excluído. ${r.data.lessons_cleared} aula(s) ficaram sem vídeo.` : "Vídeo excluído.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Biblioteca</h1>
          <p className="text-slate-500 mt-1">Vídeos e arquivos (PDF, Excel, Word…) da escola, organizados em pastas. Os vídeos vão para as aulas em Nova aula → Vídeo → "Escolher da biblioteca". Só o admin vê esta área.</p>
        </div>
        <div className="flex gap-2">
          <RefreshButton onClick={load} />
          <Button onClick={() => setUploading((v) => !v)} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid="library-upload">
            <Upload className="h-4 w-4 mr-1.5" /> Enviar vídeo, imagem ou arquivo
          </Button>
        </div>
      </div>

      {uploading && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="library-upload-box">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-800">Enviar vídeo ou arquivo {pasta ? <span className="text-sm font-normal text-slate-500">· vai para a pasta <strong>{pasta.name}</strong></span> : <span className="text-sm font-normal text-slate-500">· sem pasta (escolha uma pasta abaixo antes, se quiser)</span>}</div>
            <button onClick={() => setUploading(false)} className="p-1 rounded text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
          <VideoUpload docs folderId={pasta ? pasta.id : ""} onDone={() => { load(); }} />
          <p className="text-xs text-slate-400">Vídeo MP4 (H.264) até 6 GB · Imagem JPG, PNG, WEBP ou GIF · PDF, Excel (xlsx/xls/csv), Word ou PowerPoint até 200 MB.</p>
        </div>
      )}

      {data && (
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs text-slate-500">Vídeos · imagens · arquivos</div>
            <div className="text-2xl font-bold text-slate-900">{data.videos.filter((v) => !isDoc(v)).length} <span className="text-slate-400 text-lg">·</span> {data.videos.filter(isImg).length} <span className="text-slate-400 text-lg">·</span> {data.videos.filter((v) => isDoc(v) && !isImg(v)).length}</div>
            {unused.length > 0 && <div className="text-xs text-amber-700 mt-0.5">{unused.length} sem aula</div>}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs text-slate-500">Espaço ocupado na biblioteca</div>
            <div className="text-2xl font-bold text-slate-900">{fmtSize(data.total_size)}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs text-slate-500 flex items-center gap-1"><HardDrive className="h-3 w-3" /> Livre no servidor</div>
            <div className="text-2xl font-bold text-slate-900">{data.disk_free ? fmtSize(data.disk_free) : "—"}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="flex flex-wrap items-center gap-2" data-testid="library-folders">
          {[["todas", "Todas", data.videos.length], ["sem", "Sem pasta", contar("sem")], ...folders.map((f) => [f.id, f.name, contar(f.id)])].map(([id, nome, n]) => (
            <button key={id} onClick={() => setFolder(id)} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium ${folder === id ? "bg-[#232D4F] border-[#232D4F] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`} data-testid={`folder-${id}`}>
              <Folder className="h-4 w-4" /> {nome} <span className={`text-xs ${folder === id ? "text-white/70" : "text-slate-400"}`}>{n}</span>
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setFolderDlg({ name: "" })} data-testid="folder-new"><FolderPlus className="h-4 w-4 mr-1" /> Nova pasta</Button>
          <Button size="sm" variant="outline" onClick={baixarZip} disabled={!(data.videos || []).some(inFolder)} title="Baixa tudo o que está nesta pasta num arquivo .zip" data-testid="folder-zip">
            <FileArchive className="h-4 w-4 mr-1" /> Baixar {folder === "todas" ? "tudo" : "pasta"} (.zip)</Button>
          {pasta && <>
            <Button size="sm" variant="ghost" onClick={() => setFolderDlg({ id: pasta.id, name: pasta.name })}><Pencil className="h-4 w-4 mr-1" /> Renomear pasta</Button>
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => excluirPasta(pasta)}><Trash2 className="h-4 w-4 mr-1" /> Excluir pasta</Button>
          </>}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[["todos", "Tudo"], ["video", "Vídeos"], ["img", "Imagens"], ["doc", "Arquivos"]].map(([k, l]) => (
              <button key={k} onClick={() => setTipo(k)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${tipo === k ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`} data-testid={`tipo-${k}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[["todos", "Todos"], ["uso", "Em uso"], ["sem", "Sem aula"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${filter === k ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por nome, aula ou curso..." className="pl-9 h-10" />
          </div>
        </div>

        {data === null ? (
          <div className="p-4 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center">
            <Film className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{data.videos.length ? (pasta ? `A pasta "${pasta.name}" está vazia. Clique em Enviar vídeo ou arquivo para salvar nela.` : "Nada com esse filtro.") : "Nada ainda. Clique em Enviar vídeo ou arquivo."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto iep-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
                <th className="px-4 py-3">Nome</th><th className="px-3 py-3 hidden sm:table-cell">Duração</th>
                <th className="px-3 py-3 hidden sm:table-cell">Tamanho</th><th className="px-3 py-3">Usado em</th>
                <th className="px-3 py-3 hidden md:table-cell">Enviado em</th><th className="px-4 py-3 text-right">Opções</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {pag.itens.map((v) => {
                  const used = v.lessons.filter((l) => l.uses_it);
                  return (
                    <tr key={v.id} data-testid={`video-${v.id}`}>
                      <td className="px-4 py-2.5">
                        <button onClick={() => (isImg(v) ? setFoto(v) : isDoc(v) ? openDoc(v) : play(v))} className="flex items-center gap-2 font-medium text-slate-800 hover:text-[#1E9E2A] text-left">
                          {isImg(v) ? <img src={fileUrl(v)} alt="" loading="lazy" className="h-10 w-14 object-cover rounded border border-slate-200 bg-slate-50 shrink-0" />
                            : isDoc(v) ? <DocIcon v={v} className="h-5 w-5 shrink-0" /> : <PlayCircle className="h-5 w-5 text-slate-400 shrink-0" />} <span className="line-clamp-2">{v.name}</span>
                        </button>
                        {folder === "todas" && v.folder_id && folders.find((f) => f.id === v.folder_id) && <div className="text-[11px] text-slate-400 flex items-center gap-1 ml-7"><Folder className="h-3 w-3" /> {folders.find((f) => f.id === v.folder_id).name}</div>}
                      </td>
                      <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600">{isDoc(v) ? <span className="uppercase text-xs font-semibold text-slate-500">{v.ext}</span> : v.duration ? fmtDuration(v.duration) : "—"}</td>
                      <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600 whitespace-nowrap">{fmtSize(v.size)}</td>
                      <td className="px-3 py-2.5">
                        {isDoc(v) ? <span className="text-xs text-slate-400">—</span> : used.length === 0 ? <span className="text-xs font-medium px-2 py-1 rounded border text-amber-700 bg-amber-50 border-amber-200">Sem aula</span> : (
                          <div className="space-y-0.5">
                            {used.slice(0, 3).map((l) => !l.course_id ? <div key={l.lesson_id} className="text-xs text-slate-600">{l.lesson_title} <span className="text-slate-400">· {l.course_title}</span></div> : (
                              <Link key={l.lesson_id} to={`/admin/cursos/${l.course_id}/aulas/${l.lesson_id}/editar`} className="block text-xs text-slate-600 hover:text-[#1E9E2A] hover:underline max-w-[260px] truncate" title={`${l.course_title} › ${l.module_title} › ${l.lesson_title}`}>
                                {l.lesson_title} <span className="text-slate-400">· {l.course_title}</span>
                              </Link>
                            ))}
                            {used.length > 3 && <div className="text-xs text-slate-400">+{used.length - 3} aula(s)</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 hidden md:table-cell text-slate-600">{fmtDate(v.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`video-menu-${v.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {isImg(v) && <DropdownMenuItem onSelect={() => setFoto(v)}><ImageIcon className="h-4 w-4 mr-2" /> Ver imagem</DropdownMenuItem>}
                            {isDoc(v)
                              ? <DropdownMenuItem onSelect={() => (isImg(v) ? window.open(fileUrl(v), "_blank") : openDoc(v))}><Download className="h-4 w-4 mr-2" /> {v.ext === "pdf" ? "Abrir" : isImg(v) ? "Abrir em nova aba" : "Baixar"}</DropdownMenuItem>
                              : <DropdownMenuItem onSelect={() => play(v)}><PlayCircle className="h-4 w-4 mr-2" /> Assistir</DropdownMenuItem>}
                            <DropdownMenuItem onSelect={() => setRename({ id: v.id, name: v.name })}><Pencil className="h-4 w-4 mr-2" /> Renomear</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => setMove({ v, folder_id: v.folder_id || "" })}><FolderInput className="h-4 w-4 mr-2" /> Mover para pasta</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={() => del(v)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Paginacao {...pag} rotulo="itens" className="px-4 py-3 border-t border-slate-100" />
            <p className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100">Total de {shown.length} item(ns). Clique no vídeo para assistir, na imagem para ver ou no arquivo para abrir/baixar; clique na aula para editá-la.</p>
          </div>
        )}
      </div>

      <Dialog open={!!watch} onOpenChange={(o) => !o && setWatch(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display pr-6">{watch?.video.name}</DialogTitle></DialogHeader>
          {watch?.url
            ? <video src={watch.url} controls autoPlay controlsList="nodownload" disablePictureInPicture className="w-full aspect-video rounded-lg bg-black" />
            : <div className="aspect-video grid place-items-center bg-slate-100 rounded-lg"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rename} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Renomear</DialogTitle>
            <DialogDescription>O nome é só para organizar a biblioteca (o aluno vê o título da aula).</DialogDescription>
          </DialogHeader>
          {rename && (
            <div className="space-y-1.5 py-2"><Label>Nome</Label>
              <Input autoFocus value={rename.name} onChange={(e) => setRename((p) => ({ ...p, name: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && saveName()} data-testid="video-rename-input" /></div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRename(null)}>Cancelar</Button>
            <Button onClick={saveName} disabled={busy} className="text-white" style={{ background: "#1E9E2A" }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!foto} onOpenChange={(o) => !o && setFoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle className="font-display pr-6 truncate">{foto?.name}</DialogTitle></DialogHeader>
          {foto && <div className="grid place-items-center bg-slate-100 rounded-lg p-2"><img src={fileUrl(foto)} alt={foto.name} className="max-h-[70vh] w-auto object-contain rounded" data-testid="library-image-view" /></div>}
          <DialogFooter><Button variant="outline" onClick={() => window.open(fileUrl(foto), "_blank")}><Download className="h-4 w-4 mr-1.5" /> Abrir em nova aba</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!folderDlg} onOpenChange={(o) => !o && setFolderDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{folderDlg?.id ? "Renomear pasta" : "Nova pasta"}</DialogTitle></DialogHeader>
          {folderDlg && <div className="space-y-1.5 py-2"><Label>Nome da pasta</Label>
            <Input autoFocus value={folderDlg.name} onChange={(e) => setFolderDlg((p) => ({ ...p, name: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && salvarPasta()} placeholder="Ex.: Contratos, Planilhas, Material Básico" data-testid="folder-name" /></div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDlg(null)}>Cancelar</Button>
            <Button onClick={salvarPasta} disabled={busy} className="text-white" style={{ background: "#1E9E2A" }} data-testid="folder-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!move} onOpenChange={(o) => !o && setMove(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Mover para pasta</DialogTitle><DialogDescription className="truncate">{move?.v.name}</DialogDescription></DialogHeader>
          {move && <div className="space-y-1 py-2 max-h-[50vh] overflow-y-auto">
            {[{ id: "", name: "Sem pasta" }, ...folders].map((f) => (
              <button key={f.id || "sem"} onClick={() => setMove((m) => ({ ...m, folder_id: f.id }))} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm ${move.folder_id === f.id ? "bg-[#232D4F] text-white" : "hover:bg-slate-50 text-slate-700"}`}>
                <Folder className="h-4 w-4" /> {f.name}</button>
            ))}
            {folders.length === 0 && <p className="text-xs text-slate-500 px-1 pt-2">Nenhuma pasta criada ainda. Use "Nova pasta".</p>}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMove(null)}>Cancelar</Button>
            <Button onClick={mover} className="text-white" style={{ background: "#1E9E2A" }} data-testid="move-save">Mover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Escolher um vídeo da biblioteca (usado no editor da aula). */
export function VideoPicker({ open, onClose, onPick }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { if (open) { setRows(null); api.get("/admin/videos").then((r) => setRows(r.data.videos.filter((v) => !["doc", "image"].includes(v.kind)))).catch(() => setRows([])); } }, [open]);
  const shown = (rows || []).filter((v) => !q || v.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Escolher da biblioteca</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar vídeo..." className="pl-9 h-10" autoFocus />
        </div>
        <div className="max-h-[50vh] overflow-y-auto iep-scroll -mx-2">
          {rows === null ? <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            : shown.length === 0 ? <p className="p-6 text-center text-sm text-slate-500">{rows.length ? "Nenhum vídeo com esse nome." : "A biblioteca está vazia. Envie um vídeo."}</p>
              : shown.map((v) => {
                const n = v.lessons.filter((l) => l.uses_it).length;
                return (
                  <button key={v.id} onClick={() => onPick(v)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-left" data-testid={`pick-${v.id}`}>
                    <Film className="h-5 w-5 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">{v.name}</div>
                      <div className="text-xs text-slate-500">{v.duration ? `${fmtDuration(v.duration)} · ` : ""}{fmtSize(v.size)} · {n ? `em ${n} aula(s)` : "sem aula"}</div>
                    </div>
                  </button>
                );
              })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
